import diwaliFireworksSound from "./diwalirocketsound.mp3";

/*
 * diwaliGrandShow.js  (v3 - realistic + optimised)
 * ---------------------------------------------------------------------------
 * Performance notes (v3):
 *  - Lower render resolution on big screens, lower particle caps.
 *  - Faster adaptive quality (reacts within ~0.5s).
 *  - Glow sprites only on larger stars, and on every other star when busy.
 *  - Smaller sky/skyline glows, fewer smoke puffs.
 *  - Background scene (stars, diyas, smoke, lanterns) repainted at ~30fps.
 *  - Brand text: one stroke call for all tails, zero cost when idle.
 *  - Pre-rendered realistic clay diya sprite (cheaper and better looking).
 *
 * Usage:
 *   const stop = startDiwaliShow(canvas, { duration: 24000, reducedMotion, onFinish });
 *   return stop;   // from a useEffect
 *
 * Optional: call preloadDiwaliSound() once on dashboard mount so the first
 * click does not stutter while the mp3 loads.
 *
 * Options: brandText, brandY, brandTimes, sound (default true)
 */

const TAU = Math.PI * 2;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const LUT_N = 14;

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const speedFor = (radius, drag) => (radius * (1 - drag)) / drag;

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgba = (color, alpha) => {
  const [r, g, b] =
    color[0] === "#" ? hexToRgb(color) : color.match(/\d+/g).map(Number);
  return `rgba(${r},${g},${b},${alpha})`;
};

/* Colour lookup tables: index 0 = hot, last = cooled ember. */
const lutCache = new Map();
const makeLut = (stops) => {
  const key = stops.join();
  if (lutCache.has(key)) return lutCache.get(key);
  const rgb = stops.map(hexToRgb);
  const out = [];
  for (let i = 0; i < LUT_N; i += 1) {
    const t = (i / (LUT_N - 1)) * (rgb.length - 1);
    const k = Math.min(rgb.length - 2, Math.floor(t));
    const f = t - k;
    const c = rgb[k].map((v, j) => Math.round(v + (rgb[k + 1][j] - v) * f));
    out.push(`rgb(${c[0]},${c[1]},${c[2]})`);
  }
  lutCache.set(key, out);
  return out;
};
const palLut = (pal) => makeLut(["#ffffff", pal[0], pal[1], pal[2], "#6b2208"]);
const SPARK_LUT = makeLut(["#ffffff", "#ffd166", "#ff8a1f", "#5a1e08"]);

/* [hot core, main, deep] */
const GOLD = ["#fff4c9", "#ffd166", "#ff9f1c"];
const PALETTES = [
  { weight: 3, colors: GOLD },
  { weight: 2, colors: ["#ffe0b5", "#ff8c1a", "#e85d04"] }, // saffron
  { weight: 2, colors: ["#ffc2d1", "#ff4d6d", "#c9184a"] }, // crimson
  { weight: 1, colors: ["#d6f6ff", "#35d7ff", "#3a86ff"] }, // sky
  { weight: 1, colors: ["#ecd2ff", "#b967ff", "#7b2cbf"] }, // violet
  { weight: 1, colors: ["#e0ffc2", "#7ae582", "#2dc653"] }, // emerald
];
const WEIGHTED_PALETTES = PALETTES.flatMap((p) =>
  Array(p.weight).fill(p.colors),
);
const PETAL_COLORS = ["#ffb703", "#fb8500", "#ffd166", "#ff6b35", "#ffcf56"];

/* Soft sprites */
const glowCache = new Map();
const getGlow = (color) => {
  let sprite = glowCache.get(color);
  if (!sprite) {
    sprite = document.createElement("canvas");
    sprite.width = 64;
    sprite.height = 64;
    const g = sprite.getContext("2d");
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.16, rgba(color, 0.9));
    grad.addColorStop(0.5, rgba(color, 0.22));
    grad.addColorStop(1, rgba(color, 0));
    g.fillStyle = grad;
    g.fillRect(0, 0, 64, 64);
    glowCache.set(color, sprite);
  }
  return sprite;
};

let smokeSprite = null;
const getSmoke = () => {
  if (!smokeSprite) {
    smokeSprite = document.createElement("canvas");
    smokeSprite.width = 96;
    smokeSprite.height = 96;
    const g = smokeSprite.getContext("2d");
    const grad = g.createRadialGradient(48, 48, 0, 48, 48, 48);
    grad.addColorStop(0, "rgba(168,158,172,0.9)");
    grad.addColorStop(0.55, "rgba(140,130,150,0.38)");
    grad.addColorStop(1, "rgba(120,110,130,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, 96, 96);
  }
  return smokeSprite;
};

/* Pre-rendered realistic clay diya (built once, reused for every lamp). */
let diyaSprite = null;
const buildDiyaSprite = () => {
  if (diyaSprite) return;
  const c = document.createElement("canvas");
  const ss = 2; // supersample
  c.width = 80 * ss;
  c.height = 48 * ss;
  const g = c.getContext("2d");
  g.scale(ss, ss);

  // spout
  g.fillStyle = "#b5501c";
  g.beginPath();
  g.moveTo(10, 20);
  g.quadraticCurveTo(4, 18, 1, 9);
  g.quadraticCurveTo(10, 13, 18, 16);
  g.closePath();
  g.fill();

  // bowl
  const body = g.createLinearGradient(0, 20, 0, 46);
  body.addColorStop(0, "#e07a35");
  body.addColorStop(0.5, "#b8501c");
  body.addColorStop(1, "#5a1e08");
  g.fillStyle = body;
  g.beginPath();
  g.moveTo(8, 20);
  g.quadraticCurveTo(12, 44, 40, 45);
  g.quadraticCurveTo(68, 44, 72, 20);
  g.closePath();
  g.fill();

  // side shading
  const side = g.createLinearGradient(8, 0, 72, 0);
  side.addColorStop(0, "rgba(255,200,140,0.35)");
  side.addColorStop(0.4, "rgba(255,200,140,0)");
  side.addColorStop(1, "rgba(30,8,2,0.45)");
  g.fillStyle = side;
  g.fill();

  // gold dots
  g.fillStyle = "#ffd98a";
  for (let i = 0; i < 9; i += 1) {
    const t = i / 8;
    const dx = 16 + t * 48;
    const dy = 29 + Math.sin(t * Math.PI) * 5;
    g.beginPath();
    g.arc(dx, dy, 1.1, 0, TAU);
    g.fill();
  }

  // base
  g.fillStyle = "#4a1806";
  g.beginPath();
  g.ellipse(40, 45, 14, 2.4, 0, 0, TAU);
  g.fill();

  // rim + oil
  const rim = g.createLinearGradient(0, 14, 0, 26);
  rim.addColorStop(0, "#f6b072");
  rim.addColorStop(1, "#bf5c20");
  g.fillStyle = rim;
  g.beginPath();
  g.ellipse(40, 20, 32, 6, 0, 0, TAU);
  g.fill();
  const oil = g.createRadialGradient(40, 20, 1, 40, 20, 26);
  oil.addColorStop(0, "#ffcf6e");
  oil.addColorStop(0.6, "#b8661a");
  oil.addColorStop(1, "#3a1606");
  g.fillStyle = oil;
  g.beginPath();
  g.ellipse(40, 20.6, 28.5, 4.3, 0, 0, TAU);
  g.fill();

  // wick
  g.fillStyle = "#2a1608";
  g.fillRect(39, 14, 2, 6);

  diyaSprite = c;
};

/* Call once on dashboard mount so the first click doesn't stutter. */
export const preloadDiwaliSound = () => {
  try {
    const a = new window.Audio(diwaliFireworksSound);
    a.preload = "auto";
    a.load();
  } catch (error) {
    // preloading is optional
  }
};

/* ------------------------------------------------------------------ */
/* Sound                                                              */
/* ------------------------------------------------------------------ */
const createAudio = () => {
  let player = null;

  try {
    // Play the exact recording supplied by the user.
    player = new window.Audio(diwaliFireworksSound);
    player.preload = "auto";
    player.volume = 0.82;
    player.loop = false;
    player.currentTime = 0;

    const playRequest = player.play();
    if (playRequest && typeof playRequest.catch === "function") {
      playRequest.catch(() => {
        // Browsers may block playback until user activation; do not break the show.
      });
    }
  } catch (error) {
    player = null;
  }

  const stop = () => {
    if (!player) return;
    try {
      player.pause();
      player.currentTime = 0;
      player.src = "";
      player.load();
    } catch (error) {
      // Audio cleanup must never interrupt celebration cleanup.
    }
    player = null;
  };

  return {
    whoosh: () => {},
    boom: () => {},
    crackle: () => {},
    close: stop,
  };
};

export function startDiwaliShow(
  fxCanvas,
  {
    duration = 24000,
    reducedMotion = false,
    onFinish,
    brandText = "S S White Technology",
    brandY = 0.13, // vertical centre of the text, fraction of screen height
    brandTimes = [0.04, 0.28, 0.5, 0.66],
    brandRockets = 5, // how many rockets share the writing of the text
    sound = true,
  } = {},
) {
  const fx = fxCanvas.getContext("2d", { alpha: true, desynchronized: true });
  if (!fx) return () => {};

  const sceneCanvas = document.createElement("canvas");
  sceneCanvas.setAttribute("aria-hidden", "true");
  Object.assign(sceneCanvas.style, {
    position: "fixed",
    inset: "0",
    pointerEvents: "none",
    zIndex: "2",
    willChange: "transform",
  });
  if (fxCanvas.parentNode) {
    fxCanvas.parentNode.insertBefore(sceneCanvas, fxCanvas);
  }
  const sc = sceneCanvas.getContext("2d");
  if (!sc) {
    sceneCanvas.remove();
    return () => {};
  }

  /* Text layer: cleared every frame (no trail smear) so the lettering stays crisp. */
  const textCanvas = document.createElement("canvas");
  textCanvas.setAttribute("aria-hidden", "true");
  Object.assign(textCanvas.style, {
    position: "fixed",
    inset: "0",
    pointerEvents: "none",
    zIndex: "4",
    willChange: "transform",
  });
  if (fxCanvas.parentNode) {
    fxCanvas.parentNode.insertBefore(textCanvas, fxCanvas.nextSibling);
  }
  const tc = textCanvas.getContext("2d");

  const audio = sound ? createAudio() : null;

  /* ------------------------------------------------------------------ */
  /* State                                                              */
  /* ------------------------------------------------------------------ */
  let W = 0;
  let H = 0;
  let S = 1;
  let dpr = 1;
  let maxParticles = 1800;
  let skyline = null;
  let skylineHeight = 0;
  let stars = [];
  let diyas = [];

  const particles = [];
  const rockets = [];
  const flashes = [];
  const waves = [];
  const pending = [];
  const lanterns = [];
  const petals = [];
  const smoke = [];
  const textPts = [];
  let groundLights = [];
  let quality = 1; // drops automatically on slow machines
  let dtEma = 16.7;
  let qualityClock = 0;
  let sceneAcc = 0; // background scene is repainted at ~30fps
  let textDirty = false;

  const wind = rand(-0.1, 0.1);
  let elapsed = 0;
  let lastTs = 0;
  let rafId = 0;
  let active = true;
  let finished = false;
  let nextBatchAt = 650;
  let nextLanternAt = 1500;
  let lastPalette = null;

  /* ------------------------------------------------------------------ */
  /* Sizing & static scenery                                            */
  /* ------------------------------------------------------------------ */
  const buildSkyline = () => {
    skylineHeight = Math.round(Math.max(120, H * 0.21));
    const c = document.createElement("canvas");
    c.width = Math.round(W * dpr);
    c.height = Math.round(skylineHeight * dpr);
    const g = c.getContext("2d");
    g.scale(dpr, dpr);

    const horizon = g.createLinearGradient(0, 0, 0, skylineHeight);
    horizon.addColorStop(0, "rgba(255,140,40,0)");
    horizon.addColorStop(1, "rgba(255,150,50,0.34)");
    g.fillStyle = horizon;
    g.fillRect(0, 0, W, skylineHeight);

    let seed = 11;
    const r = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };

    let x = -12;
    while (x < W + 12) {
      const bw = 28 + r() * 72;
      const bh = skylineHeight * (0.24 + r() * 0.4);
      const top = skylineHeight - bh;

      g.fillStyle = "#12061f";
      g.fillRect(x, top, bw, bh);

      const kind = r();
      if (kind < 0.18) {
        const cx = x + bw / 2;
        const rr = bw * 0.42;
        g.beginPath();
        g.arc(cx, top, rr, Math.PI, 0);
        g.fill();
        g.fillRect(cx - 1, top - rr - 12, 2, 12);
      } else if (kind < 0.3) {
        const towerTop = top - skylineHeight * 0.18;
        g.fillRect(x + bw * 0.4, towerTop, bw * 0.2, skylineHeight * 0.18);
        g.beginPath();
        g.moveTo(x + bw * 0.37, towerTop);
        g.lineTo(x + bw * 0.5, towerTop - 15);
        g.lineTo(x + bw * 0.63, towerTop);
        g.fill();
      }

      g.fillStyle = "rgba(255,196,90,0.85)";
      for (let wy = top + 10; wy < skylineHeight - 18; wy += 11) {
        for (let wx = x + 6; wx < x + bw - 6; wx += 10) {
          if (r() < 0.32) g.fillRect(wx, wy, 2.2, 3.2);
        }
      }
      x += bw + r() * 6;
    }
    skyline = c;
  };

  const buildScene = () => {
    buildDiyaSprite();

    const starCount = Math.round(clamp((W * H) / 9000, 50, 170));
    stars = Array.from({ length: starCount }, () => ({
      x: rand(0, W),
      y: rand(0, H * 0.72),
      r: rand(0.5, 1.5),
      ph: rand(0, TAU),
      sp: rand(0.0008, 0.003),
    }));

    const count = clamp(Math.floor(W / 54), 8, 34);
    diyas = Array.from({ length: count }, (_, i) => {
      const centre = (count - 1) / 2;
      return {
        x: ((i + 0.5) * W) / count,
        ph: rand(0, TAU),
        litAt: 250 + (Math.abs(i - centre) / (count / 2)) * 1300,
      };
    });
    buildSkyline();
  };

  const resize = () => {
    W = window.innerWidth;
    H = window.innerHeight;
    dpr = Math.min(window.devicePixelRatio || 1, 1.25);
    // Big screens: render at 1x so fill-rate stays low.
    if (W * H * dpr * dpr > 2200000) dpr = 1;
    S = clamp(Math.min(W, H * 1.5) / 1000, 0.6, 1.35);
    maxParticles = S < 0.8 ? 1100 : 1800;

    [
      [fxCanvas, fx],
      [sceneCanvas, sc],
      [textCanvas, tc],
    ].forEach(([canvas, ctx]) => {
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
    });
    buildScene();
  };

  /* ------------------------------------------------------------------ */
  /* Particles                                                          */
  /* ------------------------------------------------------------------ */
  const density = () =>
    (particles.length > maxParticles * 0.7 ? 0.55 : 1) *
    (S < 0.8 ? 0.75 : 1) *
    quality;

  const add = (x, y, vx, vy, o) => {
    if (!o.force && particles.length >= maxParticles) return;
    particles.push({
      x,
      y,
      px: x,
      py: y,
      vx,
      vy,
      life: o.life,
      max: o.life,
      lut: o.lut,
      size: o.size || 1.6,
      grav: o.grav ?? 0.04,
      drag: o.drag ?? 0.96,
      bright: o.bright ?? 1,
      trail: Boolean(o.trail),
      flicker: Boolean(o.flicker),
      glitter: Boolean(o.glitter),
      split: Boolean(o.split),
      hot: Boolean(o.hot),
      text: Boolean(o.text),
    });
  };

  /* True 3D shell: Fibonacci sphere, projected. */
  const sphere3 = (x, y, pal, o) => {
    const lut = o.lut || palLut(pal);
    const n = Math.max(8, Math.round(o.count * density()));
    const fill = o.fill ?? 0.3;
    const base = speedFor(o.R, o.drag);
    const spin = rand(0, TAU);
    const rot = rand(0, TAU);
    const cr = Math.cos(rot);
    const sr = Math.sin(rot);

    for (let i = 0; i < n; i += 1) {
      const sy = 1 - ((i + 0.5) / n) * 2;
      const rr = Math.sqrt(1 - sy * sy);
      const th = i * GOLDEN_ANGLE + spin;
      const sx = Math.cos(th) * rr;
      const sz = Math.sin(th) * rr;
      const ux = sx * cr - sy * sr + rand(-0.05, 0.05);
      const uy = sx * sr + sy * cr + rand(-0.05, 0.05);
      const sp =
        base * (1 - fill + fill * Math.cbrt(Math.random())) * rand(0.97, 1.03);

      add(x, y, ux * sp, uy * sp, {
        life: rand(o.life[0], o.life[1]),
        lut,
        size: o.size ?? 1.7,
        grav: o.grav,
        drag: o.drag + rand(-0.007, 0.004),
        trail: o.trail,
        flicker: o.flicker,
        glitter: o.glitter,
        split: o.split,
        hot: Math.random() < 0.12,
        bright: (o.bright ?? 1) * (0.7 + 0.3 * (sz * 0.5 + 0.5)) * rand(0.8, 1),
      });
    }
  };

  const ring = (x, y, pal, o) => {
    const lut = palLut(pal);
    const n = Math.max(10, Math.round(o.count * density()));
    const tilt = o.tilt ?? rand(0.3, 1);
    const rot = o.rot ?? rand(0, Math.PI);
    const cr = Math.cos(rot);
    const sr = Math.sin(rot);
    const sp = speedFor(o.R, o.drag);
    for (let i = 0; i < n; i += 1) {
      const t = (i / n) * TAU;
      const ux = Math.cos(t) * sp;
      const uy = Math.sin(t) * sp * tilt;
      add(x, y, ux * cr - uy * sr, ux * sr + uy * cr, {
        life: rand(o.life[0], o.life[1]),
        lut,
        size: o.size ?? 1.8,
        grav: o.grav,
        drag: o.drag,
        bright: (o.bright ?? 1) * rand(0.85, 1),
        hot: Math.random() < 0.1,
      });
    }
  };

  const heart = (x, y, pal, R, bright, scale = 1) => {
    const lut = palLut(pal);
    const drag = 0.965;
    const n = Math.max(24, Math.round(110 * scale * density()));
    const k = (speedFor(R * scale, drag) / 16) * 1;
    for (let i = 0; i < n; i += 1) {
      const t = (i / n) * TAU;
      const hx = 16 * Math.sin(t) ** 3;
      const hy = -(
        13 * Math.cos(t) -
        5 * Math.cos(2 * t) -
        2 * Math.cos(3 * t) -
        Math.cos(4 * t)
      );
      add(x, y, hx * k, hy * k, {
        life: rand(80, 100),
        lut,
        size: 1.9,
        grav: 0.012,
        drag,
        bright,
        hot: Math.random() < 0.1,
      });
    }
  };

  const pistil = (x, y, pal, R, bright) =>
    sphere3(x, y, ["#ffffff", pal[0], pal[1]], {
      count: 40,
      R: R * 0.28,
      fill: 1,
      life: [40, 60],
      grav: 0.02,
      drag: 0.95,
      size: 1.3,
      bright,
    });

  /* Smoke left behind by a burst. */
  const puffs = (x, y, R, power) => {
    if (reducedMotion) return;
    const n = Math.round(rand(2, 3));
    for (let i = 0; i < n && smoke.length < 18; i += 1) {
      const a = rand(0, TAU);
      const d = rand(0, R * 0.55);
      const life = rand(420, 620);
      smoke.push({
        x: x + Math.cos(a) * d,
        y: y + Math.sin(a) * d,
        r: rand(50, 90) * S,
        grow: rand(0.06, 0.14),
        vx: wind * 0.6 + rand(-0.05, 0.05),
        vy: rand(-0.04, 0.01),
        life,
        max: life,
        alpha: 0.085 * clamp(power, 0.5, 1.2),
      });
    }
  };

  const panFor = (x) => (x / W) * 2 - 1;

  /* depth: 1 = near (big, bright), ~0.65 = far (smaller, dimmer, later boom) */
  const explode = (x, y, type, pal, size, depth = 1, part = null) => {
    const vis = size * (0.75 + 0.25 * depth);
    const R0 = 215 * S * vis;
    const bright = 0.55 + 0.45 * depth;
    const power = (0.5 + 0.5 * depth) * Math.min(1.3, size);

    flashes.push({
      x,
      y,
      c: pal[1],
      size: 340 * S * vis,
      t: 0,
      max: 20,
      power,
    });
    puffs(x, y, R0, power);

    const delay = 0.08 + (1 - y / H) * 0.28 + (1 - depth) * 0.2;
    audio?.boom(panFor(x), size * depth, delay);

    switch (type) {
      case "brandpart":
        flashes.push({
          x,
          y,
          c: "#ffd166",
          size: 420 * S,
          t: 0,
          max: 22,
          power: 1,
        });
        brandBurst(x, y, part);
        break;
      case "chrysanthemum":
        sphere3(x, y, pal, {
          count: 170 * size,
          R: R0,
          fill: 0.22,
          life: [90, 115],
          grav: 0.035,
          drag: 0.962,
          trail: true,
          bright,
        });
        pistil(x, y, pal, R0, bright);
        audio?.crackle(panFor(x), 6, delay + 0.2);
        break;
      case "willow":
        sphere3(x, y, GOLD, {
          count: 130 * size,
          R: R0 * 0.85,
          fill: 0.4,
          life: [140, 180],
          grav: 0.05,
          drag: 0.955,
          trail: true,
          glitter: true,
          size: 1.4,
          bright,
        });
        audio?.crackle(panFor(x), 10, delay + 0.4);
        break;
      case "ring":
        ring(x, y, pal, {
          count: 150 * size,
          R: R0 * 1.05,
          life: [70, 90],
          grav: 0.03,
          drag: 0.958,
          bright,
        });
        ring(x, y, pal, {
          count: 70 * size,
          R: R0 * 0.55,
          life: [55, 75],
          tilt: 1,
          grav: 0.03,
          drag: 0.955,
          bright,
        });
        break;
      case "heart":
        heart(x, y, pal, R0 * 1.05, bright);
        heart(x, y, pal, R0 * 1.05, bright, 0.55);
        break;
      case "crossette":
        sphere3(x, y, pal, {
          count: 28 * size,
          R: R0 * 0.95,
          fill: 0.1,
          life: [70, 82],
          grav: 0.035,
          drag: 0.96,
          split: true,
          trail: true,
          size: 2,
          bright,
        });
        audio?.crackle(panFor(x), 8, delay + 0.5);
        break;
      case "palm": {
        const arms = Math.floor(rand(6, 10));
        const offset = rand(0, TAU);
        const lut = palLut(GOLD);
        const drag = 0.965;
        for (let a = 0; a < arms; a += 1) {
          const angle = offset + (a / arms) * TAU;
          for (let j = 0; j < 7; j += 1) {
            const s = speedFor(R0 * (0.55 + j * 0.07), drag);
            add(x, y, Math.cos(angle) * s, Math.sin(angle) * s, {
              life: rand(95, 125),
              lut,
              size: 2,
              grav: 0.055,
              drag,
              trail: true,
              bright,
            });
          }
        }
        break;
      }
      case "saturn":
        sphere3(x, y, pal, {
          count: 90 * size,
          R: R0 * 0.6,
          fill: 0.25,
          life: [75, 95],
          grav: 0.03,
          drag: 0.958,
          bright,
        });
        ring(x, y, GOLD, {
          count: 110 * size,
          R: R0 * 1.1,
          tilt: 0.26,
          rot: rand(-0.5, 0.5),
          life: [80, 100],
          grav: 0.02,
          drag: 0.958,
          bright,
        });
        break;
      case "strobe":
        sphere3(x, y, ["#ffffff", pal[0], pal[1]], {
          count: 160 * size,
          R: R0 * 0.95,
          fill: 0.5,
          life: [95, 125],
          grav: 0.03,
          drag: 0.962,
          flicker: true,
          size: 1.9,
          bright,
        });
        audio?.crackle(panFor(x), 12, delay + 0.3);
        break;
      default:
        sphere3(x, y, pal, {
          count: 230 * size,
          R: R0,
          fill: 0.55,
          life: [70, 95],
          grav: 0.03,
          drag: 0.958,
          bright,
        });
        if (Math.random() < 0.6) pistil(x, y, pal, R0, bright);
    }
  };

  /*
   * Brand text written in light by several shells. The text is cut into
   * vertical slices; one rocket per slice bursts at the centre of its slice
   * and only writes that part. Sparks settle into the letters as glittering
   * gold embers, hold for ~2 s, then fall like gold rain.
   */
  const makeBrandLayout = () => {
    if (!brandText) return null;
    const fs = clamp(W * 0.062, 28, 96);
    const off = document.createElement("canvas");
    const octx = off.getContext("2d");
    const font = `800 ${fs}px Poppins, "Segoe UI", Arial, sans-serif`;
    octx.font = font;
    const textW = Math.ceil(octx.measureText(brandText).width);
    const tw = textW + 20;
    const th = Math.ceil(fs * 1.4);
    off.width = tw;
    off.height = th;
    octx.font = font;
    octx.textAlign = "center";
    octx.textBaseline = "middle";
    octx.fillStyle = "#fff";
    octx.fillText(brandText, tw / 2, th / 2);
    return {
      tw,
      th,
      textW,
      fs,
      data: octx.getImageData(0, 0, tw, th).data,
      // Fewer, slightly larger points = far cheaper, still crisp lettering.
      gap: Math.max(3.6, fs / 15) / Math.sqrt(Math.max(0.6, quality)),
    };
  };

  function brandBurst(cx, cy, part) {
    if (!part) return;
    const { layout, i, n } = part;
    const { tw, th, textW, data, gap } = layout;
    const pad = (tw - textW) / 2;
    const x0 = pad + (i * textW) / n;
    const x1 = pad + ((i + 1) * textW) / n;
    const textX = W / 2;
    const textY = H * brandY;

    for (let py = 0; py < th; py += gap) {
      for (let px = x0; px < x1; px += gap) {
        const jx = clamp(px + rand(-gap / 2, gap / 2), x0, x1 - 0.5);
        const jy = py + rand(-gap / 2, gap / 2);
        const ix = clamp(Math.round(jx), 0, tw - 1);
        const iy = clamp(Math.round(jy), 0, th - 1);
        if (data[(iy * tw + ix) * 4 + 3] < 128) continue;

        const dur = rand(85, 125);
        textPts.push({
          sx: cx + rand(-5, 5),
          sy: cy + rand(-5, 5),
          tx: textX + (jx - tw / 2),
          ty: textY + (jy - th / 2),
          x: cx,
          y: cy,
          vy: 0,
          age: 0,
          dur,
          fallAt: dur + rand(90, 120) + rand(0, 55),
          fallDur: rand(40, 70),
          size: Math.max(3.4, gap * 1.9),
          ph: rand(0, TAU),
        });
      }
    }

    sphere3(cx, cy, GOLD, {
      count: 60,
      R: 120 * S,
      fill: 0.3,
      life: [55, 80],
      grav: 0.03,
      drag: 0.96,
      trail: true,
    });
  }

  const updateText = (step) => {
    if (!textPts.length) {
      // Nothing to draw: clear once, then cost nothing.
      if (textDirty) {
        tc.globalCompositeOperation = "source-over";
        tc.globalAlpha = 1;
        tc.clearRect(0, 0, W, H);
        textDirty = false;
      }
      return;
    }
    textDirty = true;

    tc.globalCompositeOperation = "source-over";
    tc.globalAlpha = 1;
    tc.clearRect(0, 0, W, H);

    tc.globalCompositeOperation = "lighter";
    tc.lineCap = "round";
    tc.strokeStyle = "#ffd166";
    tc.lineWidth = 1.4;
    const glow = getGlow("#ffd166");

    tc.beginPath(); // ONE path for all tails
    for (let i = textPts.length - 1; i >= 0; i -= 1) {
      const p = textPts[i];
      p.age += step;
      const ox = p.x;
      const oy = p.y;
      let alpha = 1;
      let tail = false;

      if (p.age < p.dur) {
        const f = p.age / p.dur;
        const e = 1 - (1 - f) * (1 - f);
        p.x = p.sx + (p.tx - p.sx) * e;
        p.y = p.sy + (p.ty - p.sy) * e;
        alpha = 0.35 + 0.65 * f;
        tail = true;
      } else if (p.age < p.fallAt) {
        p.x = p.tx;
        p.y = p.ty;
        alpha = 0.85 + 0.15 * Math.sin(elapsed * 0.02 + p.ph * 3);
      } else {
        const fade = 1 - (p.age - p.fallAt) / p.fallDur;
        if (fade <= 0) {
          textPts[i] = textPts[textPts.length - 1];
          textPts.pop();
          continue;
        }
        p.vy += 0.045 * step;
        p.y += p.vy * step;
        p.x += wind * 0.5 * step;
        alpha = fade;
        tail = true;
      }

      if (tail) {
        tc.moveTo(ox, oy);
        tc.lineTo(p.x, p.y);
      }
      tc.globalAlpha = alpha;
      tc.drawImage(glow, p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
    }
    tc.globalAlpha = 0.4;
    tc.stroke();
  };

  /* ------------------------------------------------------------------ */
  /* Launching & choreography                                           */
  /* ------------------------------------------------------------------ */
  const pickPalette = () => {
    let pal = pick(WEIGHTED_PALETTES);
    if (pal === lastPalette) pal = pick(WEIGHTED_PALETTES);
    lastPalette = pal;
    return pal;
  };

  const launch = (
    xf,
    yf,
    type,
    size = 1,
    delay = 0,
    palette = null,
    part = null,
  ) => {
    pending.push({
      at: elapsed + delay,
      xf,
      yf,
      type,
      size,
      pal: palette || pickPalette(),
      depth: palette ? 1 : rand(0.68, 1),
      part,
    });
  };

  const spawnRocket = (job) => {
    if (rockets.length > 40) return;
    rockets.push({
      x: W * job.xf,
      y: H + 10,
      ty: H * job.yf,
      vx: job.part ? 0 : rand(-0.7, 0.7) * S,
      vy: -H * (job.part ? 0.015 : rand(0.0135, 0.0165)),
      t: 0,
      wob: rand(0, TAU),
      type: job.type,
      size: job.size,
      pal: job.pal,
      depth: job.depth,
      part: job.part,
    });
    audio?.whoosh(panFor(W * job.xf));
  };

  const getAct = () => {
    if (elapsed < duration * 0.27) return 1;
    if (elapsed < duration * 0.55) return 2;
    if (elapsed < duration - 5200) return 3;
    return 4;
  };

  const launchBatch = () => {
    const act = getAct();
    const typesByAct = {
      1: ["peony", "peony", "chrysanthemum", "ring"],
      2: [
        "peony",
        "chrysanthemum",
        "ring",
        "crossette",
        "saturn",
        "heart",
        "palm",
      ],
      3: [
        "chrysanthemum",
        "ring",
        "crossette",
        "saturn",
        "palm",
        "strobe",
        "willow",
        "heart",
      ],
      4: [
        "chrysanthemum",
        "peony",
        "willow",
        "palm",
        "saturn",
        "ring",
        "crossette",
        "strobe",
      ],
    };
    const count =
      reducedMotion || act === 1
        ? 1
        : act === 2
        ? Math.random() < 0.4
          ? 2
          : 1
        : act === 3
        ? Math.random() < 0.5
          ? 3
          : 2
        : 2;

    for (let i = 0; i < count; i += 1) {
      launch(
        rand(0.07, 0.93),
        rand(0.12, 0.4),
        pick(typesByAct[act]),
        rand(act === 1 ? 0.85 : 0.95, act === 4 ? 1.45 : 1.3),
        i * rand(60, 140),
      );
    }
  };

  const batchInterval = () => {
    if (reducedMotion) return 1500;
    return [0, 560, 380, 300, 115][getAct()];
  };

  const events = [
    {
      t: 150,
      fn: () => {
        launch(0.2, 0.3, "peony", 1.3, 0, GOLD);
        launch(0.5, 0.2, "chrysanthemum", 1.45, 120, GOLD);
        launch(0.8, 0.3, "peony", 1.3, 240, GOLD);
      },
    },
    ...brandTimes.map((fraction) => ({
      t: duration * fraction,
      fn: () => {
        const layout = makeBrandLayout();
        if (!layout) return;
        const n = Math.max(2, Math.round(brandRockets));
        for (let k = 0; k < n; k += 1) {
          const centreX = W / 2 + ((k + 0.5) / n - 0.5) * layout.textW;
          launch(centreX / W, brandY, "brandpart", 1, k * 180, GOLD, {
            layout,
            i: k,
            n,
          });
        }
      },
    })),
    {
      t: duration * 0.38,
      fn: () => {
        launch(0.3, 0.26, "heart", 1.3, 0, ["#ffc2d1", "#ff4d6d", "#c9184a"]);
        launch(0.7, 0.26, "heart", 1.3, 260, ["#ffe0b5", "#ff8c1a", "#e85d04"]);
      },
    },
    {
      t: duration * 0.5,
      fn: () => {
        const types = ["ring", "chrysanthemum", "saturn", "peony", "palm"];
        for (let i = 0; i < 9; i += 1) {
          launch(
            0.06 + i * 0.11,
            0.14 + Math.abs(4 - i) * 0.03 + rand(0, 0.06),
            types[i % types.length],
            1.2,
            i * 120,
          );
        }
      },
    },
    {
      t: duration - 3100,
      fn: () => {
        for (let i = 0; i < 11; i += 1) {
          launch(
            0.04 + i * 0.092,
            rand(0.14, 0.27),
            "willow",
            1.25,
            i * 80,
            GOLD,
          );
        }
      },
    },
    {
      t: duration - 2150,
      fn: () => {
        launch(0.5, 0.17, "saturn", 1.5, 0, GOLD);
        launch(0.28, 0.24, "chrysanthemum", 1.4, 140);
        launch(0.72, 0.24, "chrysanthemum", 1.4, 140);
        launch(0.5, 0.3, "heart", 1.45, 360, ["#ffc2d1", "#ff4d6d", "#c9184a"]);
      },
    },
  ].map((event) => ({ ...event, done: false }));

  /* ------------------------------------------------------------------ */
  /* Fountains, lanterns, petals                                        */
  /* ------------------------------------------------------------------ */
  const goldLut = palLut(GOLD);

  const emitFountains = (step) => {
    groundLights = [];
    if (reducedMotion || elapsed < 250 || elapsed > duration - 800) return;
    const k = H / 900;
    const inner = getAct() >= 2;
    const sources = [
      { xf: 0.03, lean: 0.16 },
      { xf: 0.97, lean: -0.16 },
      ...(inner
        ? [
            { xf: 0.18, lean: 0.06 },
            { xf: 0.82, lean: -0.06 },
          ]
        : []),
    ];
    const perFrame = Math.max(1, Math.round((S < 0.8 ? 2 : 4) * step));
    sources.forEach((src) => {
      groundLights.push(W * src.xf);
      for (let i = 0; i < perFrame; i += 1) {
        const angle = -Math.PI / 2 + rand(-0.4, 0.4) + src.lean;
        const speed = rand(7, 11.5) * k;
        add(
          W * src.xf + rand(-5, 5),
          H - 14,
          Math.cos(angle) * speed,
          Math.sin(angle) * speed,
          {
            life: rand(38, 62),
            lut: goldLut,
            size: rand(1.2, 2.2),
            grav: 0.14,
            drag: 0.99,
            bright: rand(0.7, 1),
          },
        );
      }
    });
  };

  const spawnLantern = () => {
    lanterns.push({
      x: rand(W * 0.08, W * 0.92),
      y: H + 50,
      vy: -rand(0.4, 0.85) * (H / 900),
      s: rand(0.8, 1.3) * clamp(S, 0.8, 1.2),
      ph: rand(0, TAU),
    });
  };

  const spawnPetal = () => {
    petals.push({
      x: rand(-20, W + 20),
      y: -14,
      vy: rand(1.1, 2.6) * (H / 900),
      sway: rand(0.4, 1.4),
      ph: rand(0, TAU),
      rot: rand(0, TAU),
      spin: rand(-0.05, 0.05),
      r: rand(5, 10) * clamp(S, 0.8, 1.2),
      c: pick(PETAL_COLORS),
    });
  };

  /* ------------------------------------------------------------------ */
  /* Update + draw: fireworks layer                                     */
  /* ------------------------------------------------------------------ */
  const updateFx = (step) => {
    fx.globalCompositeOperation = "destination-out";
    fx.globalAlpha = 1;
    fx.fillStyle = `rgba(0,0,0,${Math.min(0.6, 0.2 * step)})`;
    fx.fillRect(0, 0, W, H);
    fx.globalCompositeOperation = "lighter";
    fx.lineCap = "round";

    /* Rockets */
    for (let i = rockets.length - 1; i >= 0; i -= 1) {
      const r = rockets[i];
      r.t += step;
      const burn = 0.55 + 0.45 * Math.min(1, r.t / 18);
      r.x += (r.vx + Math.sin(r.t * 0.2 + r.wob) * 0.25 * S) * step;
      r.y += r.vy * burn * step;
      r.vy *= Math.pow(0.992, step);

      for (let k = 0; k < 3; k += 1) {
        add(r.x + rand(-1, 1), r.y + 5, rand(-0.35, 0.35), rand(0.4, 1.5), {
          life: rand(18, 34),
          lut: SPARK_LUT,
          size: 1.1,
          grav: 0.035,
          drag: 0.95,
          bright: rand(0.6, 1),
        });
      }

      fx.globalAlpha = 0.9;
      fx.drawImage(getGlow("#ffb347"), r.x - 12, r.y - 12, 24, 24);

      if (r.y <= r.ty || r.vy * burn > -1.2) {
        explode(r.x, r.y, r.type, r.pal, r.size, r.depth, r.part);
        rockets.splice(i, 1);
      }
    }

    /* Burst flash (bloom) */
    for (let i = flashes.length - 1; i >= 0; i -= 1) {
      const f = flashes[i];
      f.t += step;
      const p = f.t / f.max;
      if (p >= 1) {
        flashes.splice(i, 1);
        continue;
      }
      const size = f.size * (0.5 + p * 0.7);
      fx.globalAlpha = (1 - p) * (1 - p) * 0.85;
      fx.drawImage(getGlow(f.c), f.x - size / 2, f.y - size / 2, size, size);
    }

    /* Shockwave rings */
    fx.lineWidth = 1.5;
    fx.strokeStyle = "#fff4d8";
    for (let i = waves.length - 1; i >= 0; i -= 1) {
      const w = waves[i];
      w.t += step;
      const p = w.t / w.max;
      if (p >= 1) {
        waves.splice(i, 1);
        continue;
      }
      fx.globalAlpha = (1 - p) * 0.32 * w.power;
      fx.beginPath();
      fx.arc(w.x, w.y, w.rMax * (1 - (1 - p) * (1 - p)), 0, TAU);
      fx.stroke();
    }

    /* Particles */
    const heavy = particles.length > 900;
    let lastColor = "";
    for (let i = particles.length - 1; i >= 0; i -= 1) {
      const p = particles[i];
      p.px = p.x;
      p.py = p.y;
      const drag = Math.pow(p.drag, step);
      p.vx *= drag;
      p.vy = p.vy * drag + p.grav * step;
      p.x += p.vx * step + (p.text ? 0 : wind * 0.5 * step);
      p.y += p.vy * step;
      p.life -= step;

      if (p.split && p.life <= p.max * 0.5) {
        p.split = false;
        const base = Math.atan2(p.vy, p.vx);
        for (let k = 0; k < 4; k += 1) {
          const ang = base + Math.PI / 4 + (k * Math.PI) / 2;
          add(p.x, p.y, Math.cos(ang) * 3.4 * S, Math.sin(ang) * 3.4 * S, {
            life: rand(28, 40),
            lut: p.lut,
            size: 1.5,
            grav: 0.05,
            drag: 0.965,
            bright: p.bright,
            hot: true,
          });
        }
        p.life = 0;
      }

      if (p.life <= 0 || p.y > H + 40) {
        particles[i] = particles[particles.length - 1];
        particles.pop();
        continue;
      }

      if (
        p.trail &&
        Math.random() < 0.15 * step &&
        particles.length < maxParticles * 0.8
      ) {
        add(
          p.x,
          p.y,
          p.vx * 0.1 + rand(-0.4, 0.4),
          p.vy * 0.1 + rand(-0.2, 0.5),
          {
            life: rand(16, 30),
            lut: p.lut,
            size: 0.9,
            grav: 0.02,
            drag: 0.96,
            bright: p.bright * 0.7,
          },
        );
      }

      const a = p.life / p.max;
      let alpha = p.text
        ? Math.min(1, a * 5) *
          (0.9 + 0.1 * Math.sin(elapsed * 0.012 + p.x * 0.05))
        : Math.min(1, a * 2.4) * p.bright;
      if (p.flicker && Math.random() < 0.45) alpha *= 0.12;
      if (p.glitter && a < 0.55 && Math.random() < 0.4) alpha *= rand(0.15, 1);

      const color =
        p.lut[Math.min(LUT_N - 1, Math.floor(Math.pow(1 - a, 0.8) * LUT_N))];
      if (color !== lastColor) {
        fx.strokeStyle = color;
        lastColor = color;
      }
      fx.globalAlpha = alpha;
      fx.lineWidth = Math.max(0.6, p.size * (0.45 + 0.55 * a));
      fx.beginPath();
      fx.moveTo(p.px, p.py);
      fx.lineTo(p.x, p.y);
      fx.stroke();

      /* Burning head: glow only on bigger stars, and on every other star when busy. */
      if (p.size > 1.2 && alpha > 0.08 && (!heavy || (i & 1) === 0 || p.hot)) {
        const gs = p.size * 3.6 + (p.hot && a > 0.6 ? 7 : 3);
        fx.globalAlpha = alpha * 0.6;
        fx.drawImage(getGlow(color), p.x - gs / 2, p.y - gs / 2, gs, gs);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Draw: scene layer                                                  */
  /* ------------------------------------------------------------------ */
  const drawLantern = (l) => {
    const w = l.s * 30;
    const h = l.s * 40;
    const x = l.x + Math.sin(elapsed * 0.0009 + l.ph) * 16 * l.s;
    const y = l.y;
    const flick = 0.85 + 0.15 * Math.sin(elapsed * 0.02 + l.ph * 3);

    sc.globalCompositeOperation = "lighter";
    sc.globalAlpha = 0.55 * flick;
    const gs = w * 4.6;
    sc.drawImage(getGlow("#ff9f1c"), x - gs / 2, y - gs / 2, gs, gs);

    sc.globalCompositeOperation = "source-over";
    sc.globalAlpha = 0.95;
    const body = sc.createLinearGradient(x, y - h / 2, x, y + h / 2);
    body.addColorStop(0, "#ffe29a");
    body.addColorStop(0.5, "#ff8a1f");
    body.addColorStop(1, "#c8460f");
    sc.fillStyle = body;
    sc.beginPath();
    sc.moveTo(x - w * 0.35, y - h / 2);
    sc.lineTo(x + w * 0.35, y - h / 2);
    sc.bezierCurveTo(
      x + w * 0.7,
      y - h * 0.15,
      x + w * 0.7,
      y + h * 0.15,
      x + w * 0.4,
      y + h / 2,
    );
    sc.lineTo(x - w * 0.4, y + h / 2);
    sc.bezierCurveTo(
      x - w * 0.7,
      y + h * 0.15,
      x - w * 0.7,
      y - h * 0.15,
      x - w * 0.35,
      y - h / 2,
    );
    sc.fill();
    sc.fillStyle = "#7a2e0b";
    sc.fillRect(x - w * 0.37, y - h / 2 - 2, w * 0.74, 3);
    sc.fillRect(x - w * 0.42, y + h / 2 - 1, w * 0.84, 3);

    sc.globalCompositeOperation = "lighter";
    sc.globalAlpha = 0.8 * flick;
    sc.drawImage(getGlow("#fff0b0"), x - w * 0.7, y - h * 0.5, w * 1.4, h);
  };

  const drawDiya = (d) => {
    const lit = clamp((elapsed - d.litAt) / 250, 0, 1);
    const s = clamp(S, 0.7, 1.2);
    const x = d.x;
    const y = H - 12;

    sc.globalCompositeOperation = "source-over";
    sc.globalAlpha = 1;
    if (diyaSprite) {
      // Realistic clay lamp: bowl, spout, gold dots, oil surface and wick.
      const k = 0.55 * s;
      sc.drawImage(diyaSprite, x - 22 * s, y - 17 * s, 80 * k, 48 * k);
    }

    if (lit <= 0) return;

    const flick = reducedMotion
      ? 1
      : 1 +
        0.12 * Math.sin(elapsed * 0.012 + d.ph) +
        0.08 * Math.sin(elapsed * 0.031 + d.ph * 2);

    sc.globalCompositeOperation = "lighter";
    sc.globalAlpha = 0.7 * lit;
    const gs = 70 * s * flick;
    sc.drawImage(getGlow("#ffb347"), x - gs / 2, y - 18 * s - gs / 2, gs, gs);
    sc.globalAlpha = 0.3 * lit;
    sc.drawImage(getGlow("#ff9f1c"), x - 34 * s, y - 22 * s, 68 * s, 40 * s);

    sc.globalCompositeOperation = "source-over";
    sc.globalAlpha = lit;
    const base = y - 6 * s;
    const fh = 16 * s * flick * lit;
    const flame = (width, height, color) => {
      sc.fillStyle = color;
      sc.beginPath();
      sc.moveTo(x, base - height);
      sc.bezierCurveTo(
        x + width,
        base - height * 0.5,
        x + width * 0.7,
        base,
        x,
        base,
      );
      sc.bezierCurveTo(
        x - width * 0.7,
        base,
        x - width,
        base - height * 0.5,
        x,
        base - height,
      );
      sc.fill();
    };
    flame(6 * s, fh, "#ff9d1c");
    flame(3 * s, fh * 0.6, "#fff3b0");
  };

  /* Strength of burst light reaching a point (0..~1) plus its colour. */
  const lightAt = (x, y) => {
    let best = 0;
    let colour = null;
    const reach = 420 * S;
    for (let i = 0; i < flashes.length; i += 1) {
      const f = flashes[i];
      const p = f.t / f.max;
      const dx = x - f.x;
      const dy = y - f.y;
      const k =
        (f.power * (1 - p) * (1 - p)) /
        (1 + (dx * dx + dy * dy) / (reach * reach));
      if (k > best) {
        best = k;
        colour = f.c;
      }
    }
    return { k: best, colour };
  };

  const drawScene = (step) => {
    sc.globalCompositeOperation = "source-over";
    sc.globalAlpha = 1;
    sc.clearRect(0, 0, W, H);

    /* Stars */
    sc.fillStyle = "#fff8e1";
    stars.forEach((star) => {
      const tw = reducedMotion
        ? 0.7
        : 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(elapsed * star.sp + star.ph));
      sc.globalAlpha = tw;
      sc.fillRect(star.x, star.y, star.r, star.r);
    });
    sc.globalAlpha = 1;

    /* Sky lit by bursts */
    sc.globalCompositeOperation = "lighter";
    flashes.forEach((f) => {
      const p = f.t / f.max;
      sc.globalAlpha = (1 - p) * (1 - p) * 0.26 * f.power;
      const size = 650 * S;
      sc.drawImage(getGlow(f.c), f.x - size / 2, f.y - size / 2, size, size);
    });

    /* Ground fountain glow */
    sc.globalAlpha = 0.32;
    groundLights.forEach((gx) => {
      const gs = 190 * S * (0.9 + 0.1 * Math.sin(elapsed * 0.03 + gx));
      sc.drawImage(getGlow("#ffb347"), gx - gs / 2, H - gs * 0.35, gs, gs);
    });

    lanterns.forEach(drawLantern);

    /* Smoke drifts, lit by nearby bursts */
    sc.globalCompositeOperation = "source-over";
    for (let i = smoke.length - 1; i >= 0; i -= 1) {
      const s = smoke[i];
      s.life -= step;
      if (s.life <= 0) {
        smoke.splice(i, 1);
        continue;
      }
      s.x += s.vx * step;
      s.y += s.vy * step;
      s.r += s.grow * step;
      const fade = Math.sin(Math.PI * (1 - s.life / s.max));
      sc.globalCompositeOperation = "source-over";
      sc.globalAlpha = s.alpha * fade;
      sc.drawImage(getSmoke(), s.x - s.r, s.y - s.r, s.r * 2, s.r * 2);

      const light = lightAt(s.x, s.y);
      if (light.k > 0.02) {
        sc.globalCompositeOperation = "lighter";
        sc.globalAlpha = Math.min(0.5, light.k * 0.55) * fade;
        sc.drawImage(
          getGlow(light.colour),
          s.x - s.r,
          s.y - s.r,
          s.r * 2,
          s.r * 2,
        );
      }
    }

    /* Skyline, rim-lit by bursts */
    sc.globalCompositeOperation = "source-over";
    sc.globalAlpha = 1;
    if (skyline) {
      sc.drawImage(skyline, 0, H - skylineHeight, W, skylineHeight);
      if (flashes.length) {
        sc.save();
        sc.beginPath();
        sc.rect(0, H - skylineHeight, W, skylineHeight);
        sc.clip();
        sc.globalCompositeOperation = "lighter";
        flashes.forEach((f) => {
          const p = f.t / f.max;
          sc.globalAlpha = (1 - p) * (1 - p) * 0.14 * f.power;
          const size = 600 * S;
          sc.drawImage(
            getGlow(f.c),
            f.x - size / 2,
            H - skylineHeight / 2 - size / 2,
            size,
            size,
          );
        });
        sc.restore();
      }
    }

    diyas.forEach(drawDiya);

    /* Petals */
    sc.globalCompositeOperation = "source-over";
    petals.forEach((p) => {
      p.y += p.vy * step;
      p.x += Math.sin(elapsed * 0.002 + p.ph) * p.sway * step;
      p.rot += p.spin * step;
      sc.save();
      sc.globalAlpha = 0.92;
      sc.translate(p.x, p.y);
      sc.rotate(p.rot);
      sc.scale(1, 0.4 + 0.6 * Math.abs(Math.sin(elapsed * 0.004 + p.ph)));
      sc.fillStyle = p.c;
      sc.beginPath();
      sc.ellipse(0, 0, p.r, p.r * 0.55, 0, 0, TAU);
      sc.fill();
      sc.restore();
    });
    sc.globalAlpha = 1;
  };

  /* ------------------------------------------------------------------ */
  /* Main loop                                                          */
  /* ------------------------------------------------------------------ */
  const frame = (ts) => {
    if (!active) return;
    rafId = window.requestAnimationFrame(frame);

    if (!lastTs) lastTs = ts;
    const dtMs = Math.min(50, Math.max(0, ts - lastTs));
    lastTs = ts;
    const step = dtMs / (1000 / 60) || 0.0001;
    elapsed += dtMs;

    events.forEach((event) => {
      if (!event.done && elapsed >= event.t && elapsed < duration - 1500) {
        event.done = true;
        if (!reducedMotion || event.t < 1000) event.fn();
      }
    });

    if (elapsed < duration - 2000) {
      while (elapsed >= nextBatchAt) {
        launchBatch();
        nextBatchAt += batchInterval();
      }
    }

    for (let i = pending.length - 1; i >= 0; i -= 1) {
      if (pending[i].at <= elapsed) {
        spawnRocket(pending[i]);
        pending.splice(i, 1);
      }
    }

    if (!reducedMotion) {
      if (
        elapsed >= nextLanternAt &&
        elapsed < duration - 5500 &&
        lanterns.length < 10
      ) {
        spawnLantern();
        nextLanternAt += 1100;
      }
      if (
        elapsed > duration - 7500 &&
        elapsed < duration - 800 &&
        petals.length < 130
      ) {
        const count = Math.random() < 0.6 ? 2 : 1;
        for (let i = 0; i < count; i += 1) spawnPetal();
      }
      for (let i = lanterns.length - 1; i >= 0; i -= 1) {
        lanterns[i].y += lanterns[i].vy * step;
        if (lanterns[i].y < -90) lanterns.splice(i, 1);
      }
      for (let i = petals.length - 1; i >= 0; i -= 1) {
        if (petals[i].y > H + 20) petals.splice(i, 1);
      }
    }

    /* Adaptive quality: reacts within ~0.5s if this machine is struggling. */
    dtEma = dtEma * 0.9 + dtMs * 0.1;
    qualityClock += dtMs;
    if (qualityClock > 500) {
      qualityClock = 0;
      if (dtEma > 20) quality = Math.max(0.4, quality - 0.15);
      else if (dtEma < 17.5) quality = Math.min(1, quality + 0.05);
    }

    emitFountains(step);

    /* Background scene moves slowly: repaint at ~30fps. */
    sceneAcc += step;
    if (sceneAcc >= 1.5) {
      drawScene(sceneAcc);
      sceneAcc = 0;
    }

    updateFx(step);
    updateText(step);

    if (!finished && elapsed >= duration) {
      finished = true;
      if (typeof onFinish === "function") onFinish();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Boot + cleanup                                                     */
  /* ------------------------------------------------------------------ */
  resize();
  window.addEventListener("resize", resize);
  rafId = window.requestAnimationFrame(frame);

  return () => {
    active = false;
    if (rafId) window.cancelAnimationFrame(rafId);
    window.removeEventListener("resize", resize);
    particles.length = 0;
    rockets.length = 0;
    pending.length = 0;
    smoke.length = 0;
    textPts.length = 0;
    fx.clearRect(0, 0, W, H);
    sceneCanvas.remove();
    textCanvas.remove();
    audio?.close();
  };
}
